<template>
    <vx-popover v-if="dueHint" hover teleport arrow placement="top" class="min-w-0">
        <vx-badge data-test="terms" :color="dueHint.color" size="xs">
            {{ termsLabels[props.terms] }}
        </vx-badge>
        <template v-slot:content>
            <div class="max-w-[220px] text-center">
                <div data-test="terms-due" class="font-semibold">{{ termsDueLabel(props.termsDue) }}</div>
                <div data-test="terms-due-explanation">{{ dueHint.explanation }}</div>
            </div>
        </template>
    </vx-popover>
    <vx-badge v-else-if="termsBadge" data-test="terms" :color="termsBadge.color" size="xs">
        {{ termsLabels[props.terms] }}
        <font-awesome-icon v-if="termsBadge.icon" widthAuto :icon="termsBadge.icon" />
    </vx-badge>
    <span v-else data-test="terms" class="text-slate-500">{{ termsLabels[props.terms] }}</span>
</template>

<script lang="ts" setup>
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBadge, VxPopover, type VxBadgeColor } from '@voxie/frontend-components';
import { computed } from 'vue';
import type { KioskTeamGroupTerms, KioskTeamGroupTermsDue } from '~/services/adminTeamGroupsClient.types';
import { termsDueLabel, termsLabels } from './kioskTeamGroupsTableHelpers';

interface TermsBadge {
    color: VxBadgeColor;
    icon: IconDefinition | null;
}

interface DueHint {
    color: VxBadgeColor;
    explanation: string;
}

const termsBadges: Record<KioskTeamGroupTerms, TermsBadge | null> = {
    accepted: { color: 'sky', icon: faCheck },
    not_accepted: { color: 'slate', icon: null },
    no_terms: null,
};

const dueHints: Record<Exclude<KioskTeamGroupTermsDue, ''>, DueHint> = {
    upcoming: {
        color: 'amber',
        explanation: 'The terms are not in effect yet. The owner must accept them to avoid interruptions in service.',
    },
    overdue: {
        color: 'rose',
        explanation: 'The terms are now in effect. The owner must accept them to restore service.',
    },
};

const props = withDefaults(
    defineProps<{
        terms: KioskTeamGroupTerms;
        termsDue?: KioskTeamGroupTermsDue;
    }>(),
    {
        termsDue: '',
    }
);

const termsBadge = computed(() => termsBadges[props.terms]);

const dueHint = computed(() =>
    props.terms === 'not_accepted' && props.termsDue !== '' ? dueHints[props.termsDue] : null
);
</script>
